/**
 * Domain types for the Lernpfad curriculum engine.
 *
 * DELIBERATELY FRAMEWORK-FREE. No React, no Next.js, no Azure, no session
 * imports anywhere under `engine/` — the whole point is that
 * `(EmployeeProfile, Catalog, Constraints) → Curriculum` is a pure function
 * that could be lifted onto SAP BTP (or into a test) as-is. See the plan's
 * "Portability" section.
 */

// Type-only, and type-only in the other direction too: `policy.ts` describes its
// fields with the unions declared here, and `Constraints` carries a policy. Both
// imports are erased at compile time, so the cycle exists in the type graph
// only — there is no runtime import edge and no initialisation order to get
// wrong.
import type { LearningEvidence } from "./evidence"
import type { ComposerPolicy } from "./policy"

// ============================================================================
// Skills
// ============================================================================

/**
 * Proficiency on a 0–4 scale. The numbers are the contract: gap size is
 * `required - actual`, so the scale has to be ordinal and evenly spaced.
 */
export const SKILL_LEVELS = [0, 1, 2, 3, 4] as const
export type SkillLevel = (typeof SKILL_LEVELS)[number]

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
    0: "keine",
    1: "Grundlagen",
    2: "Anwendung",
    3: "Fortgeschritten",
    4: "Experte",
}

export type SkillCategory = "regulatorik" | "fach" | "fuehrung" | "digital" | "methodik"

export interface Skill {
    id: string
    label: string
    category: SkillCategory
}

/** What an employee currently has. */
export interface SkillHolding {
    skillId: string
    level: SkillLevel
    /**
     * Where this reading came from. Carried all the way into the rationale —
     * "laut Ihrem MAG" is a materially different sentence from "laut Ihrem
     * Stellenprofil", and the demo's Datenquellen toggle filters on it.
     */
    source: SkillSource
    /** Verbatim snippet backing the reading (MAG sentence, CV line). */
    evidence?: string
}

export type SkillSource = "stellenprofil" | "lernhistorie" | "cv" | "mag" | "selbsteinschaetzung"

/** What a role (or an aspiration) demands. */
export interface SkillRequirement {
    skillId: string
    level: SkillLevel
    /** Why this is required — drives the rationale wording and the ordering. */
    origin: RequirementOrigin
    /** Set only for `regulatorik`: the obligation that mandates it. */
    obligationId?: string
}

export type RequirementOrigin = "rolle" | "regulatorik" | "ziel" | "mag"

// ============================================================================
// Regulatory obligations — the spine
// ============================================================================

/**
 * A mandatory training obligation attached to a role, not to a skill gap.
 *
 * Modelled separately from `SkillRequirement` because obligations behave
 * differently in every step: they can never be dropped by the budget, they
 * carry a hard deadline, and they RE-appear on expiry even when the skill is
 * long since held. A "gap" framing cannot express "you know this perfectly
 * well but your FIDLEG attestation lapses in March".
 */
export interface Obligation {
    id: string
    label: string
    /** Legal/regulatory basis, shown verbatim in the UI. */
    basis: string
    /** Roles this binds. Matched against `EmployeeProfile.roleId`. */
    appliesToRoles: string[]
    /** Additional binding by signing authority, e.g. Kreditkompetenz. */
    appliesToSigningAuthority?: string[]
    /** The Baustein that discharges it. */
    bausteinId: string
    /** Months a completion stays valid. `null` = one-off, never expires. */
    validForMonths: number | null
    /** Days before expiry at which it becomes due (planning lead time). */
    leadTimeDays: number
}

// ============================================================================
// Bausteine (training building blocks)
// ============================================================================

export type BausteinFormat =
    | "e-learning"
    | "praesenz"
    | "webinar"
    | "selbststudium"
    | "on-the-job"
    /** Mentoring, Hospitation, Lernpartnerschaft, Fachzirkel — the 70-20-10 "20". */
    | "social"

export const BAUSTEIN_FORMAT_LABELS: Record<BausteinFormat, string> = {
    "e-learning": "E-Learning",
    praesenz: "Präsenz",
    webinar: "Webinar",
    selbststudium: "Selbststudium",
    "on-the-job": "On-the-job",
    social: "Soziales Lernen",
}

/**
 * The three 70-20-10 modalities.
 *
 * A property of the FORMAT, not a second field on the Baustein — otherwise the
 * two can disagree and nothing catches it. The mapping itself is a policy field,
 * because where "Selbststudium" belongs is a judgement an L&D team is entitled
 * to make differently.
 */
export type Modality = "on-the-job" | "social" | "formal"

// ============================================================================
// Evidence — what a completion actually proves
// ============================================================================

/**
 * The evidence ladder, as an ordinal scale.
 *
 * One scale serving two frameworks at once, which is why a framework switch can
 * be a single field rather than a rewrite:
 *
 *   abschluss          attendance / completion     Kirkpatrick L1-ish · LTEM tier 2
 *   wissenstest        knowledge check             Kirkpatrick L2     · LTEM tier 4
 *   entscheidungstest  decision-making competence  Kirkpatrick L2     · LTEM tier 5
 *   transfernachweis   deliverable applied at work Kirkpatrick L2→3   · LTEM tier 6
 *
 * Thalheimer's whole argument is that the first two do not validate learning.
 * Raising the policy's `evidence.floor` from `wissenstest` to
 * `entscheidungstest` IS that argument, executable: the same transcript stops
 * clearing the bar, coverage falls, and the plans grow.
 *
 * It is also what FIDLEG Art. 6 asks for. A client advisor must *evidence*
 * knowledge of the conduct rules and re-evidence it at least every 24 months —
 * an attendance record is not a Nachweis.
 */
export type EvidenceInstrument =
    | "abschluss"
    | "wissenstest"
    | "entscheidungstest"
    | "transfernachweis"

/** Fixed ordinal scale — deliberately NOT tunable. A reordered ladder is a bug. */
export const EVIDENCE_RANK: Record<EvidenceInstrument, number> = {
    abschluss: 0,
    wissenstest: 1,
    entscheidungstest: 2,
    transfernachweis: 3,
}

export const EVIDENCE_LADDER: EvidenceInstrument[] = [
    "abschluss",
    "wissenstest",
    "entscheidungstest",
    "transfernachweis",
]

/**
 * New World Kirkpatrick's required drivers — the processes that "reinforce,
 * monitor, encourage and reward" a critical behaviour on the job. They are the
 * manager's half of Level 3, and the reason confirmed transfer runs far above
 * the unsupported baseline.
 */
export type RequiredDriver = "monitor" | "reinforce" | "encourage" | "reward"

export const REQUIRED_DRIVERS: RequiredDriver[] = [
    "monitor",
    "reinforce",
    "encourage",
    "reward",
]

export interface Baustein {
    id: string
    title: string
    description: string
    /** Skills this teaches, with the level a completion certifies. */
    teaches: Array<{ skillId: string; level: SkillLevel }>
    durationHours: number
    format: BausteinFormat
    /** Baustein ids that must be completed first. */
    prerequisites: string[]
    /** Entry level — prevents recommending Grundlagen to an expert. */
    level: SkillLevel
    language: "de" | "fr" | "it" | "en"
    provider: string
    /**
     * What finishing this Baustein actually proves. Absent ⇒ the policy's
     * per-format fallback. Kept optional so all 51 existing entries stay valid.
     */
    evidence?: EvidenceInstrument
    /** Known price per participant. Absent ⇒ the policy rate card computes one. */
    costUsd?: number
}

export interface Catalog {
    skills: Skill[]
    bausteine: Baustein[]
    obligations: Obligation[]
    /** roleId → required skills. */
    roleRequirements: Record<string, SkillRequirement[]>
    /** Aspiration id → the skills that target role demands on top. */
    aspirationRequirements: Record<string, SkillRequirement[]>
}

// ============================================================================
// Employee
// ============================================================================

export interface CompletedBaustein {
    bausteinId: string
    /** ISO date. */
    completedOn: string
}

export interface EmployeeProfile {
    id: string
    displayName: string
    /** Org-chart node parent — the manager. */
    managerId: string | null
    orgUnit: string
    orgUnitLabel: string
    kurzzeichen: string
    roleId: string
    roleLabel: string
    /** Employment percentage, 10–100. Drives the time budget. */
    pensum: number
    /** Years in the current professional field — feeds level-awareness. */
    yearsInField: number
    signingAuthority?: string
    skills: SkillHolding[]
    completed: CompletedBaustein[]
    /**
     * What is known about each completion beyond the fact of it — reaction,
     * score, transfer deliverable, manager confirmation. Optional: a roster
     * imported before any of it was collected is still a valid profile, and the
     * engine's default policy asks for none of it.
     */
    evidence?: LearningEvidence[]
    /** Aspiration id from `Catalog.aspirationRequirements`, if stated. */
    aspiration?: string
    /** Synthetic German artefacts. Present only for the curated cast. */
    artefacts?: {
        cv?: string
        mag?: string
    }
}

// ============================================================================
// Constraints (the demo's live levers)
// ============================================================================

export interface Constraints {
    /**
     * Planning reference date (ISO). A lever: moving it forward expires a
     * refresher and pulls the obligation back into the plan.
     */
    asOf: string
    /** Planning horizon in months. */
    horizonMonths: number
    /**
     * Learning hours available per quarter AT 100% Pensum. The effective budget
     * is scaled by the employee's Pensum — that scaling is the demo's single
     * most persuasive moment, so it lives in the engine, not the UI.
     */
    hoursPerQuarterAtFullTime: number
    /**
     * Which evidence sources may be used. The "Datenquellen" toggle: switching
     * `mag` off must still produce a valid plan from role + obligations alone.
     *
     * A HARD gate, independent of `policy.sources.trust` — a source that is off
     * is not read at all, whatever its credibility.
     */
    allowedSources: SkillSource[]
    /**
     * How the composer should weigh, rank, sequence and budget — the HR
     * Steuerung page's output. Omitted or partial input resolves against
     * `DEFAULT_POLICY`, which reproduces the pre-parameterised engine exactly.
     *
     * It lives here rather than in a fourth argument for one reason: seven
     * exported functions take `constraints`, and a call site that FORGETS an
     * optional fourth argument silently falls back to defaults with no type
     * error. A slider that quietly does nothing on one of five views is
     * invisible in a screenshot. It also belongs here on the merits —
     * `Constraints` is already the reproducibility record for a plan that has to
     * be defensible to an auditor, and the tuning that produced the plan is part
     * of that record.
     */
    policy?: ComposerPolicy
}

// ============================================================================
// Output
// ============================================================================

export interface Gap {
    skillId: string
    skillLabel: string
    category: SkillCategory
    current: SkillLevel
    required: SkillLevel
    /** `required - current`, always > 0 for a gap. */
    size: number
    origin: RequirementOrigin
    /** Evidence for the current level, when a source supplied one. */
    evidence?: string
    evidenceSource?: SkillSource
}

/** An obligation that is due (never completed, or the completion has lapsed). */
export interface DueObligation {
    obligation: Obligation
    /** ISO date the current attestation lapses; null when never completed. */
    expiresOn: string | null
    /** ISO date by which the Baustein must be done. */
    dueBy: string
    /** True when `dueBy` is already in the past at `asOf`. */
    overdue: boolean
}

export type CurriculumItemReason =
    | "pflicht"
    | "luecke"
    | "ziel"
    | "voraussetzung"
    /** A short spaced booster after an earlier item — only when spacing is on. */
    | "auffrischung"

export interface CurriculumItem {
    baustein: Baustein
    reason: CurriculumItemReason
    /** Quarter index within the horizon, 0-based. */
    quarter: number
    /** Hard deadline (ISO) for `pflicht` items; null otherwise. */
    dueBy: string | null
    /** Gaps this Baustein closes. Empty for a pure prerequisite. */
    closesGaps: Gap[]
    /** The obligation discharged, for `pflicht` items. */
    dischargesObligation?: Obligation
    /** Baustein ids this unlocked for. Set for `voraussetzung` items. */
    prerequisiteFor?: string[]
    /**
     * Plain-German explanation. Filled by the LLM step; the engine leaves it
     * undefined and the UI falls back to a deterministic sentence, so the demo
     * still works with the backend unreachable.
     */
    rationale?: string
}

export interface QuarterPlan {
    /** 0-based index within the horizon. */
    index: number
    label: string
    /** Effective budget for this quarter after Pensum scaling. */
    budgetHours: number
    plannedHours: number
    items: CurriculumItem[]
}

export interface Curriculum {
    employeeId: string
    asOf: string
    horizonMonths: number
    /** Effective hours/quarter after Pensum scaling. */
    budgetHoursPerQuarter: number
    quarters: QuarterPlan[]
    items: CurriculumItem[]
    /** Gaps no Baustein in the catalog can close — the L&D catalog-gap signal. */
    uncoveredGaps: Gap[]
    /**
     * Items that matched a real gap but did not fit the budget within the
     * horizon. Surfaced rather than silently dropped — "kein stiller Verlust"
     * is the whole reason the composer is deterministic.
     */
    deferred: CurriculumItem[]
    totalHours: number
}
